import { useEffect } from 'react';
import { useDispatch, useSelector } from 'react-redux';
import { BrowserRouter, Routes, Route } from 'react-router-dom';
import {
  Navbar,
  BoutonSignalement,
  BandeauCookies,
  MetaDeLaPage,
  NonTrouve,
  Accueil,
  Callback,
  ListeEleves,
  FormulaireEleve,
  GrilleMatieres,
  Progression,
  CalendrierEleve,
  JeuxEleve,
  ControlesEleve,
  ControleFiche,
  EpreuveFiche,
  Chat,
  Fiches,
  Fiche,
  Evaluations,
  Dictees,
  ComprehensionsOrales,
  ExpressionsOrales,
  ExpressionsEcrites,
  PageEleve,
  Admin,
  MonProfil,
  Tarifs,
  Contact,
  MentionsLegales,
  Confidentialite,
  ConditionsVente,
  TousLesAvis,
  Desabonnement,
  DonnerAvis,
  Footer,
  RouteProtegee,
  VerrouEleve,
  VerrouMaintenance,
  VerrouAffluence,
  EntreeEleve,
  ScanMobile,
  BandeauInfo,
} from '../../components';
import { initAuth } from '../../lib/actions/authActions';
import { authService } from '../../lib/storage/authService';
import { sessionEleve } from '../../lib/storage/sessionEleve';

/**
 * Page servie dans l'iframe de renouvellement silencieux du jeton.
 * Ne rend rien : elle transmet le résultat à la fenêtre parente.
 */
function SilentRenew() {
  useEffect(() => {
    authService.completeSilentRenew().catch(() => {});
  }, []);

  return null;
}

export const BaseApp = () => {
  const dispatch = useDispatch();
  const { authentifie } = useSelector((state) => state.auth);

  // `state.auth.authentifie` ne couvre que la session OpenID Connect du
  // parent. Un enfant connecté avec son code a sa propre session, tenue à
  // part (voir `sessionEleve.js`) — même vérification que la barre de
  // navigation pour savoir qui a le droit de voir le bouton.
  const eleve = sessionEleve();

  // Restaure la session avant tout rendu de route protégée.
  useEffect(() => {
    dispatch(initAuth());
  }, [dispatch]);

  return (
    <BrowserRouter>
      {/* LE RIDEAU ENVELOPPE TOUT, BARRE ET PIED DE PAGE COMPRIS.

          Le placer plus bas laisserait la navigation autour de la page
          d'attente : un visiteur cliquerait « Tarifs » et retomberait sur la
          même page, ce qui se lit comme un site cassé plutôt qu'un site en
          travaux. */}
      {/* LA FILE D'ATTENTE ENVELOPPE LE RIDEAU, ET NON L'INVERSE.

          Un jour d'affluence, le serveur est la ressource rare ; la
          maintenance, elle, est une décision. Celui qui est refoulé faute de
          place doit voir sa file, pas une page de travaux qui lui ferait
          croire à une panne et le ferait partir.

          En pratique les deux ne se croisent presque jamais — on n'annonce pas
          une maintenance un jour d'affluence — mais l'ordre devait être
          choisi, et celui-ci dit la vérité au visiteur dans les deux cas. */}
      <VerrouAffluence>
      <VerrouMaintenance>
      {/* AU-DESSUS DE LA BARRE, ET DANS LE FLUX.

          Au-dessus, parce qu'un avis de service passe avant la navigation :
          savoir que le site ferme à 8h change ce qu'on vient y faire. Dans le
          flux et non fixé, parce que la barre est déjà collante — deux
          bandeaux superposés en permanence, c'est un tiers d'écran de
          téléphone perdu pour une phrase déjà lue. */}
      {/* LES MÉTADONNÉES DE LA PAGE COURANTE — titre, description, canonique.
          Sous le routeur, parce qu'elles suivent la route ; sans rendu,
          parce qu'elles n'écrivent que dans l'en-tête du document. Voir
          `lib/seo/pages.js` pour ce que ça corrige. */}
      <MetaDeLaPage />
      <BandeauInfo />
      <Navbar />
      {/* Le verrou enveloppe TOUTES les routes, y compris les publiques : un
          enfant qui atterrit sur la page des tarifs doit revenir à ses cours,
          pas y rester. */}
      <VerrouEleve>
      <main className="conteneur">
        <Routes>
          <Route path="/" element={<Accueil />} />

          {/* Publique : demander de créer un compte pour connaître le prix
              fait fuir avant même la première page. */}
          <Route path="/tarifs" element={<Tarifs />} />

          {/* Publique elle aussi : ceux qui ont le plus besoin d écrire sont
              souvent ceux qui n arrivent pas à entrer. */}
          <Route path="/contact" element={<Contact />} />

          {/* Obligatoires, et toujours accessibles sans compte. */}
          <Route path="/mentions-legales" element={<MentionsLegales />} />
          <Route path="/confidentialite" element={<Confidentialite />} />
          <Route path="/cgv" element={<ConditionsVente />} />

          {/* PUBLIQUE, comme la page d'accueil dont elle est le
              prolongement : un visiteur qui hésite est exactement celui
              qui vient lire les avis. */}
          <Route path="/avis" element={<TousLesAvis />} />

          {/* PUBLIQUES, ET IL LE FAUT : ce sont les pages des liens de
              courriel. Un lien ouvert depuis une messagerie arrive dans un
              onglet neuf, donc déconnecté. `DonnerAvis` fait se connecter puis
              mène au formulaire ; le désabonnement n'a besoin que du jeton. */}
          <Route path="/desabonnement" element={<Desabonnement />} />
          <Route path="/donner-mon-avis" element={<DonnerAvis />} />

          {/* Publique, comme la connexion d'un parent : c'est la porte des enfants. */}
          <Route path="/code" element={<EntreeEleve />} />

          {/* PUBLIQUE, ET IL LE FAUT : c'est la page qu'ouvre le téléphone
              après le QR code du scanner, et ce téléphone n'est connecté à
              rien. Le jeton dans l'adresse est sa seule autorisation. */}
          <Route path="/scan/:jeton" element={<ScanMobile />} />

          <Route path="/callback" element={<Callback />} />
          <Route path="/silent-renew" element={<SilentRenew />} />

          <Route
            path="/eleves"
            element={
              <RouteProtegee>
                <ListeEleves />
              </RouteProtegee>
            }
          />
          <Route
            path="/eleves/nouveau"
            element={
              <RouteProtegee>
                <FormulaireEleve />
              </RouteProtegee>
            }
          />
          {/* LA CARTE DE L’ENFANT, sur le même chemin que ses matières :
              c’est son espace à lui, et la garde de route est la même —
              un enfant ne peut ouvrir que la sienne, un parent celles de
              ses enfants. */}
          <Route
            path="/eleves/:eleveId/progression"
            element={
              <RouteProtegee>
                <Progression />
              </RouteProtegee>
            }
          />
          <Route
            path="/eleves/:eleveId/calendrier"
            element={
              <RouteProtegee>
                <CalendrierEleve />
              </RouteProtegee>
            }
          />
          <Route
            path="/eleves/:eleveId/jeux"
            element={
              <RouteProtegee>
                <JeuxEleve />
              </RouteProtegee>
            }
          />
          <Route
            path="/eleves/:eleveId/controles"
            element={
              <RouteProtegee>
                <ControlesEleve />
              </RouteProtegee>
            }
          />
          <Route
            path="/eleves/:eleveId/controles/:controleId"
            element={
              <RouteProtegee>
                <ControleFiche />
              </RouteProtegee>
            }
          />
          <Route
            path="/eleves/:eleveId/examen/:epreuveCode"
            element={
              <RouteProtegee>
                <EpreuveFiche />
              </RouteProtegee>
            }
          />

          <Route
            path="/eleves/:eleveId/matieres"
            element={
              <RouteProtegee>
                <GrilleMatieres />
              </RouteProtegee>
            }
          />
          <Route
            path="/eleves/:eleveId/matieres/:matiereId/chat"
            element={
              <RouteProtegee>
                <Chat />
              </RouteProtegee>
            }
          />

          {/* La fiche d'un enfant a sa propre adresse : elle porte trop
              d'informations pour une fenêtre, et un parent doit pouvoir y
              revenir directement. */}
          <Route
            path="/eleves/:eleveId/fiche"
            element={
              <RouteProtegee>
                <PageEleve />
              </RouteProtegee>
            }
          />

          {/* Les fiches sont rangées par matière — c'est la matière qu'on a en
              tête quand on va réviser — mais une fiche donnée a son adresse à
              elle, sous l'élève : elle se partage et se met en favori. */}
          <Route
            path="/eleves/:eleveId/matieres/:matiereId/fiches"
            element={
              <RouteProtegee>
                <Fiches />
              </RouteProtegee>
            }
          />
          {/* Les évaluations suivent le même rangement que les fiches : par
              matière, parce que c'est la matière qu'on a en tête quand on veut
              revoir ce qu'on a raté. */}
          <Route
            path="/eleves/:eleveId/matieres/:matiereId/evaluations"
            element={
              <RouteProtegee>
                <Evaluations />
              </RouteProtegee>
            }
          />

          <Route
            path="/eleves/:eleveId/fiches/:ficheId"
            element={
              <RouteProtegee>
                <Fiche />
              </RouteProtegee>
            }
          />

          {/* Les dictées, réservées aux matières de langue — même rangement
              que les évaluations : la copie s'ouvre en fenêtre, pas de page à
              elle, une dictée ne se partage pas comme une fiche. */}
          <Route
            path="/eleves/:eleveId/matieres/:matiereId/dictees"
            element={
              <RouteProtegee>
                <Dictees />
              </RouteProtegee>
            }
          />

          {/* Même rangement que les dictées, juste au-dessus : réservée aux
              matières de langue, une fiche s'ouvre en fenêtre. */}
          <Route
            path="/eleves/:eleveId/matieres/:matiereId/comprehensions-orales"
            element={
              <RouteProtegee>
                <ComprehensionsOrales />
              </RouteProtegee>
            }
          />

          {/* Les conversations d'expression orale — Camara, le 18/09/2026.
              Même famille que les trois du dessus, et même restriction aux
              matières de langue. À ne pas confondre avec la compréhension
              orale juste au-dessus : là-bas l'élève ÉCOUTE, ici il PARLE. */}
          <Route
            path="/eleves/:eleveId/matieres/:matiereId/expressions-orales"
            element={
              <RouteProtegee>
                <ExpressionsOrales />
              </RouteProtegee>
            }
          />

          {/* Les textes d'expression écrite — Camara, le 18/09/2026 :
              « faut archiver comme les autres ». Le troisième de la
              famille, et le seul où l'orthographe de l'enfant se voit. */}
          <Route
            path="/eleves/:eleveId/matieres/:matiereId/expressions-ecrites"
            element={
              <RouteProtegee>
                <ExpressionsEcrites />
              </RouteProtegee>
            }
          />

          <Route
            path="/profil"
            element={
              <RouteProtegee>
                <MonProfil />
              </RouteProtegee>
            }
          />

          <Route
            path="/admin"
            element={
              <RouteProtegee admin>
                <Admin />
              </RouteProtegee>
            }
          />

          {/* UNE VRAIE PAGE, ET NON UN RENVOI VERS L'ACCUEIL. Le renvoi
              rendait le contenu complet de l'accueil en HTTP 200 pour
              N'IMPORTE QUELLE adresse : Google pouvait indexer
              `mimia.fr/n-importe-quoi` comme une page à part entière, en
              doublon de celle qu'on veut voir remonter. Et le visiteur qui
              avait mal recopié un lien ne comprenait pas où il venait
              d'atterrir. Voir `NonTrouve`. */}
          <Route path="*" element={<NonTrouve />} />
        </Routes>
      </main>
      </VerrouEleve>
      {/* SUR TOUTE PAGE, UNE FOIS CONNECTÉ — parent ou enfant, même
          sélecteur que la barre de navigation. Un visiteur non connecté n'a
          personne à qui écrire un signalement : le serveur ne saurait pas
          quel parent prévenir. */}
      {(authentifie || eleve) && <BoutonSignalement />}

      {/* LE CONSENTEMENT, POUR TOUT LE MONDE — y compris un visiteur non
          connecté, qui est justement celui que la mesure d'audience observe.
          Le bandeau décide seul de se montrer : il se tait dans l'espace
          connecté et quand la question est tranchée. */}
      <BandeauCookies />
      <Footer />
      </VerrouMaintenance>
      </VerrouAffluence>
    </BrowserRouter>
  );
};

export default BaseApp;
